import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function TrainerComparison() {
	const { t } = useTranslation();
	const primary = "#43BD98";
	const accent = "#4A90E2";

	const rows = t("trainer.comparison.rows", { returnObjects: true });

	const fadeScale = {
		hidden: { opacity: 0, scale: 0.97, filter: "blur(8px)" },
		show: {
			opacity: 1,
			scale: 1,
			filter: "blur(0px)",
			transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
		},
	};

	const container = {
		hidden: {},
		show: { transition: { staggerChildren: 0.12 } },
	};

	return (
		<section
			className="relative pb-28 pt-12 bg-white text-gray-900 font-[Poppins] overflow-hidden"
			style={{ marginTop: "-12px" }}
		>
			<div className="max-w-6xl mx-auto px-6 text-center">
				{/* Encabezado */}
				<motion.p
					initial={{ opacity: 0, y: 15 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, ease: "easeOut" }}
					viewport={{ once: true }}
					className="uppercase text-[0.9rem] font-semibold tracking-wide mb-3"
					style={{ color: accent }}
				>
					{t("trainer.comparison.badge")}
				</motion.p>

				<motion.h2
					initial={{ opacity: 0, y: 15 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
					viewport={{ once: true }}
					style={{
						fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
						fontWeight: 800,
						margin: 0,
						marginBottom: 40,
					}}
					dangerouslySetInnerHTML={{ __html: t("trainer.comparison.title") }}
				/>

				{/* 💻 Desktop */}
				<motion.div
					variants={container}
					initial="hidden"
					whileInView="show"
					viewport={{ once: true, amount: 0.3 }}
					className="hidden md:block overflow-hidden rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.05)] border border-gray-100"
				>
					<div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700 text-[1.05rem]">
						<div className="py-4 px-6 text-left"></div>
						<div className="py-4 px-6 text-center border-l border-gray-200">
							{t("trainer.comparison.appHeader")}
						</div>
						<div className="py-4 px-6 text-center border-l border-gray-200">
							{t("trainer.comparison.webHeader")}
						</div>
					</div>

					{rows.map((row, i) => (
						<motion.div
							key={i}
							variants={fadeScale}
							className={`grid grid-cols-3 border-t border-gray-100 text-gray-700 text-[1rem] ${
								i % 2 === 0 ? "bg-white" : "bg-gray-50/60"
							}`}
						>
							<div className="py-4 px-6 font-semibold text-left">
								{row.label}
							</div>
							<div className="py-4 px-6 text-center border-l border-gray-100 text-gray-600">
								{row.app}
							</div>
							<div className="py-4 px-6 text-center border-l border-gray-100 text-gray-600">
								{row.web}
							</div>
						</motion.div>
					))}
				</motion.div>

				{/* 📱 Móvil */}
				<motion.div
					className="block md:hidden mt-6"
					variants={container}
					initial="hidden"
					whileInView="show"
					viewport={{ once: true, amount: 0.3 }}
				>
					{rows.map((row, i) => (
						<motion.div
							key={i}
							variants={fadeScale}
							className="bg-gray-50 rounded-2xl shadow-sm border border-gray-100 p-5 mb-4 text-left"
						>
							<p className="font-semibold text-gray-900 mb-2">{row.label}</p>
							<p className="text-gray-700 text-sm mb-1">
								📱 <strong>{t("trainer.comparison.appShort")}:</strong>{" "}
								{row.app}
							</p>
							<p className="text-gray-700 text-sm">
								🌐 <strong>{t("trainer.comparison.webShort")}:</strong>{" "}
								{row.web}
							</p>
						</motion.div>
					))}
				</motion.div>

				{/* 🟢 Texto final */}
				<motion.p
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
					viewport={{ once: true }}
					className="mt-12 text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto"
					dangerouslySetInnerHTML={{ __html: t("trainer.comparison.footer") }}
				/>
			</div>
		</section>
	);
}
